import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Form, Formik } from 'formik';
import { Checkbox, FormControlLabel, Paper, Typography } from '@material-ui/core';

import { useMutation } from '@apollo/client';

import { Button } from 'core/components/button';
import PaymentMethodFields from './payment-method-fields';
import { useSweepPaymentToken } from './use-sweep-payment-token';
import {
  ADD_SWEEP_PAYMENT_METHOD,
  REMOVE_SWEEP_PAYMENT_METHOD,
  SET_DEFAULT_SWEEP_PAYMENT_METHOD
} from './sweep-account.queries';
import useStyles from './sweep-account.styles';

const describePaymentMethod = (pm) => {
  const label = pm.type === 'us_bank_account' ? pm.bankName || 'Bank account' : pm.brand || 'Card';
  return `${label} •••• ${pm.last4 || '????'}`;
};

const ManageAccount = ({ account, onUpdated }) => {
  const classes = useStyles();
  const getPaymentToken = useSweepPaymentToken();
  const [removePaymentMethod] = useMutation(REMOVE_SWEEP_PAYMENT_METHOD);
  const [setDefaultPaymentMethod] = useMutation(SET_DEFAULT_SWEEP_PAYMENT_METHOD);
  const [addPaymentMethod] = useMutation(ADD_SWEEP_PAYMENT_METHOD);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const runOnMethod = async (paymentMethodId, mutation, pick) => {
    setError(null);
    setBusyId(paymentMethodId);
    try {
      const { data } = await mutation({ variables: { paymentMethodId } });
      onUpdated(data[pick]);
    } catch (e) {
      setError(e.message || 'Something went wrong');
    } finally {
      setBusyId(null);
    }
  };

  const onAdd = async (values, { setSubmitting, resetForm }) => {
    setError(null);
    try {
      const { paymentMethodToken, paymentMethodType } = await getPaymentToken(values.paymentMethodType);
      const { data } = await addPaymentMethod({
        variables: { paymentMethodToken, paymentMethodType, makeDefault: values.makeDefault }
      });
      onUpdated(data.addSweepPaymentMethod);
      resetForm();
    } catch (e) {
      setError(e.message || 'Failed to add payment method');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Paper className={classes.card} elevation={1}>
        <Typography variant="h6" gutterBottom>
          {account.contact?.name || account.affinityStem} — payment methods
        </Typography>
        {account.paymentMethods.length === 0 && (
          <Typography color="textSecondary">No payment methods on file yet.</Typography>
        )}
        {account.paymentMethods.map((pm) => (
          <div key={pm.id} className={classes.paymentMethodRow}>
            <span>
              {describePaymentMethod(pm)}
              {pm.isDefault && <span className={classes.defaultBadge}>Default</span>}
            </span>
            <span className={classes.actions} style={{ marginTop: 0 }}>
              {!pm.isDefault && (
                <Button
                  variant="outlined"
                  color="primary"
                  loading={busyId === pm.id}
                  onClick={() => runOnMethod(pm.id, setDefaultPaymentMethod, 'setDefaultSweepPaymentMethod')}
                >
                  Make default
                </Button>
              )}
              <Button
                variant="outlined"
                loading={busyId === pm.id}
                onClick={() => runOnMethod(pm.id, removePaymentMethod, 'removeSweepPaymentMethod')}
              >
                Remove
              </Button>
            </span>
          </div>
        ))}
      </Paper>

      <Paper className={classes.card} elevation={1}>
        <Typography variant="h6" gutterBottom>
          Add a payment method
        </Typography>
        <Formik initialValues={{ paymentMethodType: 'card', makeDefault: false }} onSubmit={onAdd}>
          {({ values, handleChange, isSubmitting }) => (
            <Form>
              <PaymentMethodFields />
              <FormControlLabel
                control={<Checkbox name="makeDefault" checked={values.makeDefault} onChange={handleChange} />}
                label="Set as default"
              />
              <div className={classes.actions}>
                <Button type="submit" variant="contained" color="primary" loading={isSubmitting}>
                  Add payment method
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </Paper>

      {error && (
        <Typography color="error" className={classes.error}>
          {error}
        </Typography>
      )}
    </>
  );
};

ManageAccount.propTypes = {
  account: PropTypes.object.isRequired,
  onUpdated: PropTypes.func.isRequired
};

export default ManageAccount;
